// BacklogView.tsx — first tab of /content-map (replaced the Story Map).
//
// One flat page, the Design-System grid language: every content piece is a
// uniform card, sectioned by category. Education comes first — and inside
// it, "The AI Operations Course" chapters lead in order. Consistency law
// (Erez, 2026-06-10): every card has the same shape — chips row, a
// two-line title slot, ONE line of description — so the page scans as a
// single clean surface.

import { architectureT as T } from "../blueprint/ArchitectureBlocks";
import type { Item } from "./ContentMapPage";
import { CreamPost } from "./CreamPost";

const FUNNEL_COLORS: Record<string, { fg: string; bg: string; border: string }> = {
  TOFU: { fg: T.green, bg: T.greenSoft, border: T.green },
  MOFU: { fg: T.amber, bg: T.amberSoft, border: T.amber },
  BOFU: { fg: T.plum, bg: T.plumSoft, border: T.plum },
};

const COURSE_ARC = "Knowledge Set 01";
const PINNED_FIRST = "Knowledge Publication";

function clampLine(text: string | null, max: number): string {
  if (!text) return "";
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

function BacklogCard({ item, onClick }: { item: Item; onClick: () => void }) {
  const funnel = item.funnel_stage ? FUNNEL_COLORS[item.funnel_stage] : null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      style={{
        background: T.white,
        borderRadius: 12,
        padding: 0,
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <CreamPost item={item} />
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          padding: "10px 12px",
        }}
      >
        {item.content_type && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: T.skyDark,
              background: T.skySoft,
              border: `1px solid ${T.sky}`,
              padding: "2px 7px",
              borderRadius: 6,
              whiteSpace: "nowrap",
            }}
          >
            {item.content_type}
          </span>
        )}
        {item.funnel_stage && funnel && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: funnel.fg,
              background: funnel.bg,
              border: `1px solid ${funnel.border}`,
              padding: "2px 7px",
              borderRadius: 999,
              whiteSpace: "nowrap",
            }}
          >
            {item.funnel_stage}
          </span>
        )}
        {item.scheduled_date && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 10,
              fontWeight: 600,
              color: T.ink3,
              whiteSpace: "nowrap",
            }}
          >
            {item.scheduled_date}
          </span>
        )}
      </div>
    </div>
  );
}

export function BacklogView({
  items,
  onPick,
}: {
  items: Item[];
  onPick: (item: Item) => void;
}) {
  // Section order: Education first, then by category_order.
  const byCategory = new Map<string, { order: number; items: Item[] }>();
  for (const i of items) {
    const key = i.category ?? "Uncategorized";
    const g = byCategory.get(key) ?? { order: i.category_order ?? 999, items: [] };
    g.items.push(i);
    byCategory.set(key, g);
  }
  const sections = [...byCategory.entries()].sort((a, b) => {
    if (a[0] === PINNED_FIRST) return -1;
    if (b[0] === PINNED_FIRST) return 1;
    return a[1].order - b[1].order || a[0].localeCompare(b[0]);
  });

  const sortInSection = (a: Item, b: Item) => {
    const aCourse = a.story_arc === COURSE_ARC ? 0 : 1;
    const bCourse = b.story_arc === COURSE_ARC ? 0 : 1;
    if (aCourse !== bCourse) return aCourse - bCourse;
    return (
      (a.story_order ?? 999) - (b.story_order ?? 999) ||
      (a.sort_order ?? 999999) - (b.sort_order ?? 999999) ||
      a.title.localeCompare(b.title)
    );
  };

  return (
    // data-build: cache-busting salt — bumping it forces a new Vite chunk
    // hash when the CF edge caches a poisoned /assets/* response (2026-06-10).
    <div data-build="20260610-2">
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: T.skyDark,
            marginBottom: 8,
          }}
        >
          MyJarvis · Marketing
        </div>
        <h1
          style={{
            fontSize: 30,
            fontWeight: 800,
            color: T.ink,
            margin: 0,
            letterSpacing: "-0.02em",
          }}
        >
          Backlog
        </h1>
        <p style={{ fontSize: 14, color: T.ink2, maxWidth: 720, lineHeight: 1.6 }}>
          Every content piece on one surface, sectioned by pillar. Education
          leads with the course chapters; click any card for full detail.
        </p>
      </div>

      {sections.map(([category, g]) => {
        const sorted = [...g.items].sort(sortInSection);
        return (
          <section key={category} style={{ marginBottom: 36 }}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 10,
                marginBottom: 12,
                paddingBottom: 8,
                borderBottom: `2px solid ${T.ink}`,
              }}
            >
              <h2
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: T.ink,
                  margin: 0,
                }}
              >
                {category}
              </h2>
              <span style={{ fontSize: 11, color: T.ink3, fontWeight: 600 }}>
                {sorted.length} pieces
              </span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                gap: 12,
              }}
            >
              {sorted.map((item) => (
                <BacklogCard key={item.id} item={item} onClick={() => onPick(item)} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
